// Centralized user roles configuration
// This file contains all user role-related mappings and configurations

export interface UserRoleConfig {
  code: string;           // Role code with underscore naming (e.g., 'internal_admin', 'client_employee')
  displayName: string;    // Human-readable display name (e.g., 'Internal Admin', 'Client Employee')
  description: string;    // Detailed description of the role
  permissions: string[];  // Array of permission strings for this role
}

// Translation key mapping for role display names
export const USER_ROLE_TRANSLATION_KEYS: Record<string, string> = {
  'internal_admin': 'user_role_internal_admin',
  'client_admin': 'user_role_client_admin',
  'client_employee': 'user_role_client_employee',
  'family_member': 'user_role_family_member'
};

// Available user roles - matches the database naming convention with underscores
export const AVAILABLE_USER_ROLES: UserRoleConfig[] = [
  {
    code: 'internal_admin',
    displayName: 'Internal Admin',
    description: 'Internal personnel who work for Morgen Health directly.',
    permissions: ['view_all_documents', 'view_all_users', 'manage_all_companies', 'system_admin']
  },
  {
    code: 'client_admin',
    displayName: 'Client Admin',
    description: 'The administrators of clients; mostly HR managers, but whoever is responsible.',
    permissions: ['view_company_documents', 'view_company_users', 'manage_company_users', 'invite_users']
  },
  {
    code: 'client_employee',
    displayName: 'Client Employee',
    description: 'The actual budget receivers, who are always employees of the client.',
    permissions: ['view_own_documents', 'upload_documents', 'view_family_members']
  },
  {
    code: 'family_member',
    displayName: 'Family Member',
    description: 'Family members of the clients who can also receive budgets.',
    permissions: ['view_own_documents', 'upload_documents']
  }
];

// Create lookup maps for performance
export const USER_ROLE_BY_CODE = AVAILABLE_USER_ROLES.reduce((acc, role) => {
  acc[role.code] = role;
  return acc;
}, {} as Record<string, UserRoleConfig>);

export const USER_ROLE_DISPLAY_NAMES = AVAILABLE_USER_ROLES.reduce((acc, role) => {
  acc[role.code] = role.displayName;
  return acc;
}, {} as Record<string, string>);

export const USER_ROLE_DESCRIPTIONS = AVAILABLE_USER_ROLES.reduce((acc, role) => {
  acc[role.code] = role.description;
  return acc;
}, {} as Record<string, string>);

// Helper functions
export const getUserRoleByCode = (code: string): UserRoleConfig | undefined => {
  return USER_ROLE_BY_CODE[code];
};

export const getUserRoleDisplayName = (code: string): string => {
  return USER_ROLE_DISPLAY_NAMES[code] || code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to get localized role display name
// This should be used in components with access to the translation function
export const getLocalizedUserRoleDisplayName = (code: string, t: (key: string) => string): string => {
  const translationKey = USER_ROLE_TRANSLATION_KEYS[code];
  if (translationKey) {
    return t(translationKey);
  }
  // Fallback to the default display name if no translation key is found
  return getUserRoleDisplayName(code);
};

export const getUserRoleDescription = (code: string): string => {
  return USER_ROLE_DESCRIPTIONS[code] || 'Unknown role';
};

export const getUserRolePermissions = (code: string): string[] => {
  const role = getUserRoleByCode(code);
  return role?.permissions || [];
};

// Get all role codes
export const getAllUserRoleCodes = (): string[] => {
  return AVAILABLE_USER_ROLES.map(role => role.code);
};

// Get all role display names
export const getAllUserRoleDisplayNames = (): string[] => {
  return AVAILABLE_USER_ROLES.map(role => role.displayName);
};

// Check if a role code is supported
export const isUserRoleSupported = (code: string): boolean => {
  return code in USER_ROLE_BY_CODE;
};

// Get default role
export const getDefaultUserRole = (): string => {
  return 'client_employee';
};

// Check if user has specific permission
export const hasPermission = (roleCode: string, permission: string): boolean => {
  const permissions = getUserRolePermissions(roleCode);
  return permissions.includes(permission);
};

// Get roles that have a specific permission
export const getRolesWithPermission = (permission: string): string[] => {
  return AVAILABLE_USER_ROLES
    .filter(role => role.permissions.includes(permission))
    .map(role => role.code);
};

// Legacy mappings for backward compatibility
export const USER_ROLE_OPTIONS = AVAILABLE_USER_ROLES.map(role => ({
  value: role.code,
  label: role.displayName
}));

// Helper function to create localized role options for dropdowns/selects
// This should be used in components with access to the translation function
export const getLocalizedUserRoleOptions = (t: (key: string) => string) => {
  return AVAILABLE_USER_ROLES.map(role => ({
    value: role.code,
    label: getLocalizedUserRoleDisplayName(role.code, t)
  }));
};
